import { isCompanyActionEligible } from "../../helper/companyEligibility";
import { useCallback } from "react";
import { enrichWorkspaceApi } from "../../api/workspaceApi";
import { getBatchResultsApi, getBatchStatusApi } from "../../api/enrichmentApi";
import { errorDetail } from "../../helper/errorDetail";
import { createGeneratedColumnLoadingGate } from "../../helper/generatedColumn";
import { statusPollRetryDelay } from "./batchStatusPolling";
import { enrichSubmitErrorMessage, QUOTE_SCOPE_CHANGED_MESSAGE } from "./enrichmentQuote";
import { applyOptimisticWorkspaceEnrichmentLabel, buildRunningEnrichmentStatusForCompanies, normalizeWorkspaceEnrichmentTitle } from "./savedSearchQuery";

function enrichmentCompanyIds(selectedIds, workspaceMemberIds, companies, useSnapshot) {
  const eligibleIds = useSnapshot ? workspaceMemberIds : companies
    .filter(isCompanyActionEligible).map((company) => String(company.stagedId));
  const selected = Array.from(selectedIds);
  if (selected.length) return selected.map(String).filter((id) => eligibleIds.includes(id));
  return eligibleIds;
}

function useSavedSearchEnrichment({
    setLiveEnrichmentStatusByColumn, pollTimerRef, setError, setEnriching,
    setBatchStatus, enrichPollCompletedRef, preferWorkspaceSnapshotRef, fetchWorkspaceDetail,
    isMobile, setEnrichPanelCollapsed, enrichQuery, columnTitle,
    selectedIds, workspaceMemberIds, companies, enrichQuote,
    llmProvider, workspaceEnrichmentColumns, workspaceEnrichmentColumnLabels, liveEnrichmentStatusByColumn,
    setWorkspaceEnrichmentColumns, setWorkspaceEnrichmentColumnLabels, setFocusGeneratedColumnKey, setEnrichQuery,
    setColumnTitle, id
}) {
const applyBatchResultsToLiveColumn = useCallback((columnKey, results = []) => {
    if (!columnKey || !Array.isArray(results) || results.length === 0) return;

    setLiveEnrichmentStatusByColumn((prev) => {
      const nextColumn = { ...(prev[columnKey] || {}) };
      let changed = false;

      results.forEach((result) => {
        const companyId = String(result?.company_id || "").trim();
        if (!companyId) return;
        const summary = String(result?.answer?.answer?.summary || result?.summary || "").trim();
        if (summary) {
          nextColumn[companyId] = { status: "success", result: summary };
          changed = true;
          return;
        }
        if (result?.status === "failed" || result?.error) {
          nextColumn[companyId] = {
            status: "failed",
            error: result.error || "Enrichment failed",
          };
          changed = true;
        }
      });

      if (!changed) return prev;
      return { ...prev, [columnKey]: nextColumn };
    });
  }, [setLiveEnrichmentStatusByColumn]);

const finalizeLiveColumn = useCallback((columnKey, companyIds = []) => {
    if (!columnKey) return;
    setLiveEnrichmentStatusByColumn((prev) => {
      const nextColumn = { ...(prev[columnKey] || {}) };
      companyIds.forEach((id) => {
        const stagedId = String(id || "");
        if (!stagedId) return;
        if (nextColumn[stagedId]?.status === "running") {
          nextColumn[stagedId] = { status: "failed", error: "Enrichment failed" };
        }
      });
      return { ...prev, [columnKey]: nextColumn };
    });
  }, [setLiveEnrichmentStatusByColumn]);

const pollBatchStatus = useCallback(
    async (batchId, columnKey, companyIds = [], loadingGate = null, statusFailures = 0) => {
      const stopOrRetry = async (status, message) => {
        const retryDelay = statusPollRetryDelay(status, statusFailures);
        if (retryDelay !== null) {
          pollTimerRef.current = setTimeout(
            () => pollBatchStatus(batchId, columnKey, companyIds, loadingGate, statusFailures + 1),
            retryDelay
          );
          return;
        }
        setError(message);
        if (loadingGate) await loadingGate.waitBeforeReveal();
        finalizeLiveColumn(columnKey, companyIds);
        setEnriching(false);
      };
      const loadResults = async (label) => {
        try {
          const resultsResp = await getBatchResultsApi(batchId);
          applyBatchResultsToLiveColumn(columnKey, resultsResp?.data?.results || []);
        } catch (resultsError) {
          console.error(`Failed to load ${label}:`, resultsError);
        }
      };

      try {
        const res = await getBatchStatusApi(batchId);
        if (res?.status !== 200) {
          await stopOrRetry(
            res?.status,
            errorDetail(res?.data?.detail, "Enrichment status polling failed")
          );
          return;
        }

        setBatchStatus(res.data);

        if (loadingGate) await loadingGate.waitBeforeReveal();

        applyBatchResultsToLiveColumn(
          columnKey,
          (res.data.items || []).filter((item) => item?.status === "failed")
        );

        const completedCount = Number(res.data.completed) || 0;
        if (completedCount > enrichPollCompletedRef.current) {
          enrichPollCompletedRef.current = completedCount;
          await loadResults("enrichment batch results");
        }

        if (["completed", "failed", "cancelled"].includes(res.data.status)) {
          await loadResults("final enrichment batch results");
          finalizeLiveColumn(columnKey, companyIds);
          setEnriching(false);
          preferWorkspaceSnapshotRef.current = true;
          await fetchWorkspaceDetail();
          if (res.data.status === "completed" && isMobile) {
            setEnrichPanelCollapsed(true);
          }
        } else {
          pollTimerRef.current = setTimeout(
            () => pollBatchStatus(batchId, columnKey, companyIds, loadingGate),
            3000
          );
        }
      } catch (err) {
        await stopOrRetry(
          err?.response?.status,
          "An error occurred while polling enrichment status"
        );
      }
    },
    [applyBatchResultsToLiveColumn, enrichPollCompletedRef, fetchWorkspaceDetail, finalizeLiveColumn, isMobile, pollTimerRef, preferWorkspaceSnapshotRef, setBatchStatus, setEnrichPanelCollapsed, setEnriching, setError]
  );

const handleEnrich = async () => {
    const columnKey = String(enrichQuery || "").trim();
    const submittedTitle = normalizeWorkspaceEnrichmentTitle(columnTitle);
    if (!columnKey) return;

    const companyIds = enrichmentCompanyIds(selectedIds, workspaceMemberIds, companies,
      preferWorkspaceSnapshotRef.current);

    if (!companyIds.length) {
      setError("No valid companies found to enrich.");
      return;
    }
    const quoteToken = enrichQuote.tokenFor(companyIds, llmProvider);
    if (!quoteToken) {
      setError(QUOTE_SCOPE_CHANGED_MESSAGE);
      return;
    }

    const loadingGate = createGeneratedColumnLoadingGate();
    const priorColumn = {
      existed: workspaceEnrichmentColumns.includes(columnKey),
      label: workspaceEnrichmentColumnLabels[columnKey],
      status: liveEnrichmentStatusByColumn[columnKey],
      query: enrichQuery,
      title: columnTitle,
    };

    // Optimistic column + skeletons on the same EnrichmentGrid the page renders.
    setError(null);
    setEnriching(true);
    setBatchStatus(null);
    enrichPollCompletedRef.current = 0;
    setWorkspaceEnrichmentColumns((prev) =>
      prev.includes(columnKey) ? prev : [...prev, columnKey]
    );
    setWorkspaceEnrichmentColumnLabels((prev) =>
      applyOptimisticWorkspaceEnrichmentLabel(prev, columnKey, submittedTitle));
    setLiveEnrichmentStatusByColumn((prev) => ({
      ...prev,
      [columnKey]: buildRunningEnrichmentStatusForCompanies(companyIds),
    }));
    setFocusGeneratedColumnKey(columnKey);
    setEnrichQuery("");
    setColumnTitle("");

    try {
      const payload = {
        query: columnKey,
        title: submittedTitle,
        llm_provider: llmProvider,
        company_ids: companyIds,
        quote_token: quoteToken,
      };

      const res = await enrichWorkspaceApi(id, payload);
      if (res?.status === 200 && res.data?.batch_id) {
        pollBatchStatus(res.data.batch_id, columnKey, companyIds, loadingGate);
        return;
      }

      setError(enrichSubmitErrorMessage(res));
      enrichQuote.refresh();

      await loadingGate.waitBeforeReveal();
      rollbackOptimisticColumn(columnKey, priorColumn);
      setEnriching(false);
    } catch (_err) {
      setError("An error occurred during enrichment");
      await loadingGate.waitBeforeReveal();
      rollbackOptimisticColumn(columnKey, priorColumn);
      setEnriching(false);
    }
  };

const rollbackOptimisticColumn = (columnKey, prior) => {
    const restore = (prev, value) => {
      const next = { ...prev };
      if (value === undefined) delete next[columnKey];
      else next[columnKey] = value;
      return next;
    };
    if (!prior.existed) {
      setWorkspaceEnrichmentColumns((prev) => prev.filter((key) => key !== columnKey));
    }
    setWorkspaceEnrichmentColumnLabels((prev) => restore(prev, prior.label));
    setLiveEnrichmentStatusByColumn((prev) => restore(prev, prior.status));
    setFocusGeneratedColumnKey(null);
    setEnrichQuery(prior.query);
    setColumnTitle(prior.title);
  };

return {
    handleEnrich
};
}

export { useSavedSearchEnrichment, enrichmentCompanyIds };

export function cancelSavedSearchRequests(pollTimerRef, abortControllerRef) {
  if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
  if (abortControllerRef.current) abortControllerRef.current.abort();
}
